import { type gqlV2, useQuery, useTeam } from '@vrplatform/data';
import type { Maybe } from '@vrplatform/utils';
import {
  type AutomationTemplateVisibility,
  formatUserName,
  getSyncStatus,
} from '@vrplatform/ui-common';

export const STORYBOOK_AUTOMATION_HISTORY_OVERWRITES_QUERY_KEY =
  'automation-history-overwrites';
export const STORYBOOK_AUTOMATION_HISTORY_DRAWER_QUERY_KEY =
  'automation-history-drawer';

export const getSync = (sync: gqlV2.sync) => {
  const leftConnection = sync?.automation?.leftConnection;
  const rightConnection = sync?.automation?.rightConnection;
  const extractConnection = sync.connection;

  return {
    id: sync.id,
    status: getSyncStatus(sync),
    title: sync.message,
    triggerRef: sync.triggerRef,
    createdAt: sync.createdAt,
    triggeredBy: formatUserName(sync.user || {}, {
      showEmpty: true,
    }),
    automation: {
      id: sync.automationId,
      name: sync.automation?.title || sync?.automation?.ttemplate?.title || '',
      templateType: sync?.automation?.ttemplate?.type,
      visibility: sync?.automation?.ttemplate
        ?.visibility as Maybe<AutomationTemplateVisibility>,
    },
    leftConnection: {
      id: leftConnection?.app?.id,
      icon: leftConnection?.app?.iconRound,
      name: leftConnection?.name || leftConnection?.app?.name,
    },
    rightConnection: {
      id: rightConnection?.app?.id,
      icon: rightConnection?.app?.iconRound,
      name: rightConnection?.name || rightConnection?.app?.name,
    },
    extractConnection: {
      id: sync.connectionId,
      icon: extractConnection?.app?.iconRound,
      name: extractConnection?.name || extractConnection?.app?.name,
      uniqueRef: extractConnection?.uniqueRef,
      appName: extractConnection?.app?.name,
    },
    isExtractWorkflow: !!extractConnection?.id && !sync?.automationId,
  };
};

export type Workflow = ReturnType<typeof getSync>;

export function useAutomationHistoryDrawerQuery(syncIds: string[]) {
  const [{ id: tenantId }] = useTeam();

  const {
    data: overwrites,
    isInitialLoading,
    error,
  } = useQuery(
    (q) =>
      q
        .issueMessageOverwrites({
          order_by: [{ pattern: 'asc_nulls_last' }],
        })
        .map((o) => ({
          pattern: o.pattern || '',
          message: o.message || '',
        })),
    {
      keepPreviousData: true,
      storybookQueryKey: STORYBOOK_AUTOMATION_HISTORY_OVERWRITES_QUERY_KEY,
    }
  );

  const queryData = useQuery(
    (q, args) => {
      if (!args.syncIds) return null;

      const syncs = !args.syncIds
        ? []
        : q
            .syncs({
              where: {
                id: { _in: args.syncIds },
              },
              order_by: [{ createdAt: 'desc' }],
            })
            .map<Workflow>(getSync);

      return {
        workflows: syncs,
        messageOverwrites: args.overwrites,
      };
    },
    {
      queryKey: ['tasks', 'automations', 'jobPlans'],
      skip: !syncIds?.length,
      variables: {
        syncIds,
        overwrites: overwrites || [],
        tenantId,
      },
      storybookQueryKey: STORYBOOK_AUTOMATION_HISTORY_DRAWER_QUERY_KEY,
    }
  );

  return {
    data: queryData.data,
    isLoading: queryData.isLoading || isInitialLoading,
    error: queryData.error || error,
    refetch: queryData.refetch,
  };
}
